import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Select,
  Spin,
  Modal,
  notification,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { apiRequest } from "util/services";
import {
  deleteOrgCxGptSession,
  fetchOrgCxGptSession,
  fetchOrgCxGptSessions,
  mapOrgApiSessionDetail,
  mapOrgApiSessionSummary,
} from "util/copilotSessionApi";
import {
  accessLevelLabelKey,
  avatarColorForUser,
  exportOrgSessionsCsv,
  formatRelativeActivity,
  formatSessionDateTime,
  hasAdvisorSessionMessages,
  routeContextLabel,
  userInitials,
} from "util/advisorOrgHistoryDisplay";
import {
  getSessionDateGroupKey,
  SESSION_GROUP_LABEL_KEYS,
} from "util/copilotSessionGroups";
import styles from "./style.module.scss";
import AdvisorFeedbackPanel from "./AdvisorFeedbackPanel";

const PERIOD_DAYS = {
  "90": 90,
  "30": 30,
  "7": 7,
  all: null,
};

const GROUP_ORDER = ["today", "yesterday", "last_week", "older"];

const Representation = ({ user, locale = "tr-TR", theme = "dark" }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0 });

  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("90");
  const [filterUserId, setFilterUserId] = useState(undefined);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("conversations");

  useEffect(() => {
    if (!user?.uid) return;
    apiRequest("subscriptions/all", "POST", { user_id: user.uid }, true).then(
      (result) => {
        if (!result?.error && Array.isArray(result)) {
          setIsAdmin(Boolean(result[0]?.IsOrganizationAdmin));
        }
      }
    );
  }, [user?.uid]);

  const startDate = useMemo(() => {
    const days = PERIOD_DAYS[period];
    if (!days) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  }, [period]);

  const fetchList = useCallback(
    async (page = 1, { append = false } = {}) => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const payload = {
          page,
          page_size: 50,
        };
        if (search.trim()) payload.search = search.trim();
        if (filterUserId) payload.filter_user_id = filterUserId;
        if (startDate) payload.start_date = startDate;

        const result = await fetchOrgCxGptSessions(payload);
        if (result?.success) {
          const mapped = (result.sessions || [])
            .map(mapOrgApiSessionSummary)
            .filter(hasAdvisorSessionMessages);
          setSessions((prev) => {
            if (!append) return mapped;
            const seen = new Set(prev.map((s) => `${s.ownerUserId}:${s.id}`));
            const extra = mapped.filter((s) => !seen.has(`${s.ownerUserId}:${s.id}`));
            return [...prev, ...extra];
          });
          setSummary(result.summary || null);
          setFilterUsers(result.filter_users || []);
          setPagination(result.pagination || { page: 1, page_size: 50, total: 0 });
          setForbidden(false);
          if (!append) {
            setSelected((prev) => {
              if (
                prev &&
                mapped.some(
                  (s) => s.id === prev.id && s.ownerUserId === prev.ownerUserId
                )
              ) {
                return prev;
              }
              return mapped[0] || null;
            });
          }
        } else if (result?.error && String(result.error).toUpperCase().includes("FORBIDDEN")) {
          setForbidden(true);
        } else {
          notification.error({
            message: result?.error || t("advisor_history_page.loadError"),
          });
        }
      } catch {
        notification.error({ message: t("advisor_history_page.loadError") });
      } finally {
        setLoading(false);
      }
    },
    [user?.uid, search, filterUserId, startDate, t]
  );

  const fetchDetail = useCallback(async (row) => {
    if (!row?.id || !row?.ownerUserId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const result = await fetchOrgCxGptSession(row.id, row.ownerUserId);
      if (result?.session) {
        setDetail(mapOrgApiSessionDetail(result.session));
      } else {
        setDetail(null);
        notification.error({ message: t("advisor_history_page.detailError") });
      }
    } catch {
      notification.error({ message: t("advisor_history_page.detailError") });
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  useEffect(() => {
    if (selected) {
      fetchDetail(selected);
    } else {
      setDetail(null);
    }
  }, [selected, fetchDetail]);

  const groupedSessions = useMemo(() => {
    const buckets = {};
    for (const session of sessions) {
      const key = getSessionDateGroupKey(session.updatedAt);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(session);
    }
    return GROUP_ORDER.filter((key) => buckets[key]?.length).map((key) => ({
      key,
      labelKey: SESSION_GROUP_LABEL_KEYS[key],
      sessions: buckets[key],
    }));
  }, [sessions]);

  const isOwnSession = detail?.ownerUserId === user?.uid;

  const handleDelete = () => {
    if (!detail) return;
    Modal.confirm({
      title: t("advisor_history_page.deleteConfirmTitle"),
      content: t("advisor_history_page.deleteConfirmBody", { title: detail.title }),
      okText: t("advisor_history_page.deleteOk"),
      cancelText: t("advisor_history_page.deleteCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const result = await deleteOrgCxGptSession(detail.id, detail.ownerUserId);
        if (result?.success) {
          notification.success({ message: t("advisor_history_page.deleted") });
          setSelected(null);
          setDetail(null);
          fetchList(1);
        } else {
          notification.error({
            message: result?.error || t("advisor_history_page.deleteFailed"),
          });
        }
      },
    });
  };

  const handleExport = () => {
    exportOrgSessionsCsv(
      sessions.map((s) => ({
        user: s.user,
        title: s.title,
        updated_at: formatSessionDateTime(s.updatedAt, locale),
        message_count: s.messageCount,
        last_route: s.lastRoute,
      })),
      t
    );
  };

  const renderSessionRow = (session) => {
    const active =
      selected?.id === session.id && selected?.ownerUserId === session.ownerUserId;
    const route = routeContextLabel(session.lastRoute);
    return (
      <div
        key={`${session.ownerUserId}-${session.id}`}
        className={active ? styles.sessionRowActive : styles.sessionRow}
        onClick={() => setSelected(session)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") setSelected(session);
        }}
      >
        <div className={styles.userCell}>
          <span
            className={styles.avatar}
            style={{ backgroundColor: avatarColorForUser(session.ownerUserId) }}
          >
            {userInitials(session.user?.full_name, session.user?.email)}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={styles.userName}>{session.user?.full_name}</div>
            <div className={styles.userEmail}>{session.user?.email}</div>
          </div>
        </div>
        <div>
          <div className={styles.sessionTitle}>{session.title}</div>
          <div className={styles.sessionMeta}>
            {session.messageCount} {t("advisor_history_page.messages")}
          </div>
          {route ? <span className={styles.routeTag}>{route.label}</span> : null}
        </div>
        <div className={styles.dateCell}>
          {formatSessionDateTime(session.updatedAt, locale)}
        </div>
      </div>
    );
  };

  if (forbidden) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.forbidden}>
          <h2 className={styles.title}>{t("advisor_history_page.title")}</h2>
          <p>{t("advisor_history_page.forbidden")}</p>
        </div>
      </div>
    );
  }

  const wrapperClass =
    theme === "light" || theme === "brand"
      ? `${styles.wrapper} ${styles.wrapperLight}`
      : styles.wrapper;

  return (
    <div className={wrapperClass}>
      <p className={styles.breadcrumb}>{t("advisor_history_page.breadcrumb")}</p>
      <h1 className={styles.title}>{t("advisor_history_page.title")}</h1>
      <p className={styles.subtitle}>{t("advisor_history_page.subtitle")}</p>

      <div className={styles.tabRow}>
        <button
          type="button"
          className={
            activeTab === "conversations" ? styles.tabBtnActive : styles.tabBtn
          }
          onClick={() => setActiveTab("conversations")}
        >
          {t("advisor_history_page.tabConversations")}
        </button>
        <button
          type="button"
          className={activeTab === "feedback" ? styles.tabBtnActive : styles.tabBtn}
          onClick={() => setActiveTab("feedback")}
        >
          {t("advisor_history_page.tabFeedback")}
        </button>
      </div>

      {activeTab === "feedback" ? (
        <AdvisorFeedbackPanel user={user} locale={locale} theme={theme} />
      ) : (
        <>
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.total_sessions ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.statTotal")}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.unique_users ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.statUsers")}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{summary?.sessions_this_month ?? "—"}</span>
          <span className={styles.statLabel}>{t("advisor_history_page.statMonth")}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>
            {formatRelativeActivity(summary?.last_activity_at, t, locale)}
          </span>
          <span className={styles.statLabel}>{t("advisor_history_page.statActivity")}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.searchInput}
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t("advisor_history_page.searchPlaceholder")}
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onPressEnter={() => setSearch(searchDraft)}
        />
        <Select
          value={period}
          onChange={setPeriod}
          style={{ minWidth: 140 }}
          options={[
            { value: "90", label: t("advisor_history_page.period90") },
            { value: "30", label: t("advisor_history_page.period30") },
            { value: "7", label: t("advisor_history_page.period7") },
            { value: "all", label: t("advisor_history_page.periodAll") },
          ]}
        />
        <Select
          allowClear
          placeholder={t("advisor_history_page.filterUser")}
          value={filterUserId}
          onChange={setFilterUserId}
          style={{ minWidth: 200 }}
          showSearch
          optionFilterProp="label"
          options={filterUsers.map((u) => ({
            value: u.user_id,
            label: `${u.full_name || u.email} (${u.email || u.user_id})`,
          }))}
        />
        <Button icon={<SearchOutlined />} onClick={() => setSearch(searchDraft)}>
          {t("advisor_history_page.search")}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => fetchList(1)}>
          {t("advisor_history_page.refresh")}
        </Button>
        <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!sessions.length}>
          {t("advisor_history_page.exportCsv")}
        </Button>
      </div>

      <div className={styles.split}>
        <div className={styles.listPanel}>
          <div className={styles.listHeader}>
            <span>{t("advisor_history_page.colUser")}</span>
            <span>{t("advisor_history_page.colChat")}</span>
            <span>{t("advisor_history_page.colDate")}</span>
          </div>
          <div className={styles.listScroll}>
            {loading ? (
              <div className={styles.loadingWrap}>
                <Spin />
              </div>
            ) : groupedSessions.length === 0 ? (
              <div className={styles.emptyState}>{t("advisor_history_page.empty")}</div>
            ) : (
              groupedSessions.map((group) => (
                <div key={group.key}>
                  <div className={styles.groupLabel}>{t(group.labelKey)}</div>
                  {group.sessions.map(renderSessionRow)}
                </div>
              ))
            )}
          </div>
          {pagination.total > sessions.length ? (
            <div className={styles.loadMore}>
              <Button
                type="link"
                loading={loading}
                onClick={() => fetchList(pagination.page + 1, { append: true })}
              >
                {t("advisor_history_page.loadMore")}
              </Button>
            </div>
          ) : null}
        </div>

        <div className={styles.detailPanel}>
          {detailLoading ? (
            <div className={styles.loadingWrap}>
              <Spin />
            </div>
          ) : !detail ? (
            <div className={styles.emptyState}>{t("advisor_history_page.selectPrompt")}</div>
          ) : (
            <>
              <div className={styles.detailHeader}>
                <div className={styles.detailOwner}>
                  <span
                    className={styles.detailOwnerAvatar}
                    style={{
                      backgroundColor: avatarColorForUser(detail.ownerUserId),
                    }}
                  >
                    {userInitials(detail.user?.full_name, detail.user?.email)}
                  </span>
                  <div>
                    <div className={styles.detailOwnerName}>{detail.user?.full_name}</div>
                    <div className={styles.detailOwnerEmail}>{detail.user?.email}</div>
                    <span className={styles.accessBadge}>
                      {t(accessLevelLabelKey(detail.user?.access_level))}
                    </span>
                  </div>
                </div>
                <h2 className={styles.detailTitle}>{detail.title}</h2>
                <div className={styles.metaChips}>
                  <span className={styles.metaChip}>
                    {formatSessionDateTime(detail.updatedAt, locale)}
                  </span>
                  {routeContextLabel(detail.lastRoute) ? (
                    <span className={styles.metaChip}>
                      {routeContextLabel(detail.lastRoute).label}
                    </span>
                  ) : null}
                  <span className={styles.metaChip}>
                    {detail.messageCount || detail.messages?.length || 0}{" "}
                    {t("advisor_history_page.messages")}
                  </span>
                </div>
              </div>

              <div className={styles.thread}>
                {(detail.messages || []).map((msg, index) => {
                  const isUser = msg.role === "user";
                  const label = isUser
                    ? detail.user?.full_name || t("cx_gpt.you")
                    : t("cx_gpt.panel_brand");
                  return (
                    <div
                      key={`${msg.ts || index}-${msg.role}`}
                      className={
                        isUser ? styles.messageRowUser : styles.messageRowAssistant
                      }
                    >
                      <div className={styles.messageLabel}>{label}</div>
                      <div
                        className={isUser ? styles.bubbleUser : styles.bubbleAssistant}
                      >
                        {msg.content}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles.detailFooter}>
                <span className={styles.readOnlyNote}>
                  {isOwnSession
                    ? t("advisor_history_page.ownSessionNote")
                    : t("advisor_history_page.readOnlyNote", {
                        name: detail.user?.full_name || detail.user?.email,
                      })}
                </span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {isOwnSession ? (
                    <Button
                      type="primary"
                      icon={<ArrowRightOutlined />}
                      onClick={() => history.push("/console/advisor")}
                    >
                      {t("advisor_history_page.continueInAdvisor")}
                    </Button>
                  ) : null}
                  {isAdmin ? (
                    <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                      {t("advisor_history_page.deleteChat")}
                    </Button>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Representation;
